import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { Box, Divider, Skeleton, Typography, useTheme } from '@mui/material';
import { formatNumber } from 'components/FormCreatorComponent/helpers';
import { pagesEnum } from 'constants/ui.constants';
import { format } from 'date-fns';
import { useAppSelector } from 'hooks/redux.hooks';
import { useNavigate } from 'react-router-dom';
import {
	getBeneficiaryStatusSummaryData,
	getBeneficiaryStatusSummaryLoading,
} from 'state/reducers/balance.reducer';
import { getBeneficiaryDatesRange } from 'state/reducers/dashboard.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';

export const BeneficiaryStatusBox = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const t = useAppSelector(selectTranslations);
	const summary = useAppSelector(getBeneficiaryStatusSummaryData);
	const loading = useAppSelector(getBeneficiaryStatusSummaryLoading);
	const datesRange = useAppSelector(getBeneficiaryDatesRange);
	const muted = theme.palette.root.basic3;
	const textColor = theme.palette.root.basicDeeoGreen;

	const openTable = () => {
		const query = new URLSearchParams({
			dateFrom: format(datesRange.startDate, 'yyyy-MM-dd'),
			dateTo: format(datesRange.endDate, 'yyyy-MM-dd'),
		});
		navigate(`/${pagesEnum.MAIN_DASHBOARD}/beneficiaries?${query.toString()}`);
	};

	const rows = summary
		? [
				[t('page.dashboard.beneficiaryStatus.approved'), summary.approved, '#00A76F'],
				[t('page.dashboard.beneficiaryStatus.approvedWithRisk'), summary.approvedWithRisk, '#4CC38A'],
				[t('page.dashboard.beneficiaryStatus.returnedForRepair'), summary.returnedForRepair, '#FF7A2F'],
				[t('page.dashboard.beneficiaryStatus.declined'), summary.declined, '#D63C32'],
				[t('page.dashboard.beneficiaryStatus.awaitingApproval'), summary.awaitingApproval, '#8FA3B5'],
				[t('page.dashboard.beneficiaryStatus.inCompletion'), summary.inCompletion, '#C4CFD6'],
				[t('label.suspended'), summary.suspended, '#90A4A4'],
		  ]
		: [];

	return (
		<Box
			onClick={openTable}
			role="link"
			tabIndex={0}
			onKeyDown={(event) => {
				if (event.key === 'Enter' || event.key === ' ') openTable();
			}}
			sx={{
				minHeight: 280,
				borderRadius: '8px',
				boxShadow: '0px 0px 16px rgba(0, 58, 53, 0.15)',
				p: 2,
				backgroundColor: theme.palette.background.paper,
				cursor: 'pointer',
			}}
		>
			<Box display="flex" justifyContent="space-between" alignItems="flex-start">
				<Box>
					<Box display="flex" alignItems="center" gap={1}>
						<PeopleAltIcon sx={{ color: textColor, fontSize: 20 }} />
						<Typography variant="subtitle2" sx={{ color: textColor }}>
							{t('page.dashboard.dashboardBalances.beneficiaries')}
						</Typography>
					</Box>
					{summary && (
						<Typography variant="boldExtraLarge" sx={{ color: textColor, mt: 0.5 }}>
							{formatNumber(summary.created)}
						</Typography>
					)}
				</Box>
				<Box display="flex" alignItems="center" gap={0.5} sx={{ color: muted }}>
					<Typography variant="captionRegular" sx={{ color: muted }}>
						{t('page.dashboard.beneficiaryStatus.allRecords')}
					</Typography>
					<ArrowBackIcon sx={{ fontSize: 14, color: muted }} />
				</Box>
			</Box>

			{summary ? (
				<>
					<Divider sx={{ my: 1.5, borderColor: theme.palette.grey[300] }} />
					{rows.map(([label, value, color]) => {
						const percentage =
							summary.created === 0 ? '0.0' : (((value as number) / summary.created) * 100).toFixed(1);
						return (
							<Box
								key={label as string}
								display="flex"
								justifyContent="space-between"
								alignItems="center"
								py={0.6}
							>
								<Box display="flex" alignItems="center" gap={1}>
									<Box
										sx={{
											width: 10,
											height: 10,
											borderRadius: '2px',
											backgroundColor: color,
											flexShrink: 0,
										}}
									/>
									<Typography variant="body1" sx={{ color: textColor }}>
										{label}
									</Typography>
								</Box>
								<Box
									display="flex"
									alignItems="center"
									gap={1}
									sx={{ direction: 'ltr', flexShrink: 0 }}
								>
									<Typography variant="boldStat" sx={{ color: textColor }}>
										{formatNumber(value as number)}
									</Typography>
									<Typography variant="captionRegular" sx={{ color: muted }}>
										{percentage}%
									</Typography>
								</Box>
							</Box>
						);
					})}
				</>
			) : loading ? (
				<Skeleton variant="rectangular" height={210} sx={{ mt: 1.5, borderRadius: 1 }} />
			) : null}
		</Box>
	);
};
